/**
 * Editorial formatting utilities following Illinois State University style guidelines
 * 
 * These utilities ensure consistent formatting across all Course Finder displays
 * (filters, active filters, course cards, course detail pages, etc.)
 */

/**
 * Illinois State Editorial Standards - Time Formatting
 * 
 * Converts time strings to Illinois State editorial format:
 * - lowercase a.m./p.m. (not AM/PM) 
 * - remove :00 from whole hours
 * - remove leading zeros from hours
 * - drop first a.m./p.m. if same period (e.g., "8 a.m.-10 a.m." → "8-10 a.m.")
 * 
 * Examples: "2:30 p.m." not "2:30 PM", "8-10 a.m." not "8 a.m.-10 a.m."
 */
export const formatMeetingTime = (timeString) => {
  if (!timeString) return timeString;
  
  let formatted = timeString;
  
  // Step 1: Convert AM/PM to a.m./p.m. (handle no spaces)
  formatted = formatted.replace(/AM\b/g, ' a.m.');
  formatted = formatted.replace(/PM\b/g, ' p.m.');
  
  // Step 2: Remove leading zeros from hours (02:30 → 2:30, 09:00 → 9:00)
  formatted = formatted.replace(/\b0(\d+):/g, '$1:');
  
  // Step 3: Remove :00 from whole hours (now that we have a.m./p.m.)
  formatted = formatted.replace(/(\d+):00(\s*(?:a\.m\.|p\.m\.))/g, '$1$2');
  
  // Step 4: Remove redundant first a.m./p.m. if same period (8 a.m. - 10 a.m. → 8-10 a.m.)
  formatted = formatted.replace(/(\d+(?::\d+)?) (a\.m\.) - (\d+(?::\d+)?) (a\.m\.)/g, '$1-$3 $2');
  formatted = formatted.replace(/(\d+(?::\d+)?) (p\.m\.) - (\d+(?::\d+)?) (p\.m\.)/g, '$1-$3 $2');

  // Step 5: Remove spaces around hyphen for mixed periods (11 a.m. - 12:15 p.m. → 11 a.m.-12:15 p.m.)
  formatted = formatted.replace(/(\d+(?::\d+)?\s*(?:a\.m\.|p\.m\.)) - (\d+(?::\d+)?\s*(?:a\.m\.|p\.m\.))/g, '$1-$2');

  return formatted;
};

/**
 * Human-centered time range labels
 * 
 * Converts raw time data to human-centered time ranges based on data analysis
 */
export const getTimeRangeLabel = (timeOfDay) => {
  if (!timeOfDay) return timeOfDay;
  
  const ranges = {
    'Morning': 'Morning (7 a.m.–noon)',
    'Afternoon': 'Afternoon (noon–5 p.m.)', 
    'Evening': 'Evening (5 p.m.–9 p.m.)'
  };
  
  return ranges[timeOfDay] || timeOfDay;
};

/**
 * Chronological sorting for time of day items
 *
 * Sorts time periods in chronological order (not alphabetical)
 */
export const sortTimeOfDayItems = (items) => {
  const timeOrder = ['Morning', 'Afternoon', 'Evening'];

  return [...items].sort((a, b) => {
    const indexA = timeOrder.findIndex(time => a.label.includes(time));
    const indexB = timeOrder.findIndex(time => b.label.includes(time));

    // If not found in time order, sort alphabetically at end
    if (indexA === -1 && indexB === -1) return a.label.localeCompare(b.label);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};


/**
 * Illinois State Editorial Standards - Credit Hour Formatting
 *
 * Formats credit hour displays with proper singular/plural grammar
 *
 * Examples:
 * - formatCreditHours(1) → "1 Credit Hour"
 * - formatCreditHours(3) → "3 Credit Hours"
 * - formatCreditHours("1") → "1 Credit Hour"
 * - formatCreditHours(1, 3) → "1 to 3 Credit Hours"
 * - formatCreditHours("1", "1") → "1 Credit Hour"
 */
export const formatCreditHours = (credits, maxCredits = null) => {
  const creditNum = parseFloat(credits);
  const maxCreditNum = maxCredits ? parseFloat(maxCredits) : null;

  // Handle invalid/missing data
  if (isNaN(creditNum)) return credits;

  // Range format: "1 to 3 Credit Hours"
  if (maxCreditNum && maxCreditNum !== creditNum) {
    const isPlural = maxCreditNum > 1;
    return `${creditNum} to ${maxCreditNum} Credit Hour${isPlural ? 's' : ''}`;
  }

  // Single value format: "1 Credit Hour" or "3 Credit Hours"
  const isPlural = creditNum > 1;
  return `${creditNum} Credit Hour${isPlural ? 's' : ''}`;
};

/**
 * Short format for credit hours (for compact displays)
 *
 * Examples:
 * - formatCreditHoursShort(1) → "(1 hr)"
 * - formatCreditHoursShort(3) → "(3 hrs)"
 * - formatCreditHoursShort(1, 3) → "(1-3 hrs)"
 */
export const formatCreditHoursShort = (credits, maxCredits = null) => {
  const creditNum = parseFloat(credits);
  const maxCreditNum = maxCredits ? parseFloat(maxCredits) : null;

  // Handle invalid/missing data
  if (isNaN(creditNum)) return `(${credits})`;

  // Range format: "(1-3 hrs)"
  if (maxCreditNum && maxCreditNum !== creditNum) {
    const isPlural = maxCreditNum > 1;
    return `(${creditNum}-${maxCreditNum} hr${isPlural ? 's' : ''})`;
  }

  // Single value format: "(1 hr)" or "(3 hrs)"
  const isPlural = creditNum > 1;
  return `(${creditNum} hr${isPlural ? 's' : ''})`;
};

// Grouped exports for organized importing
export const timeFormatting = {
  formatMeetingTime,
  getTimeRangeLabel,
  sortTimeOfDayItems
};

export const creditHourFormatting = {
  formatCreditHours,
  formatCreditHoursShort
};